import React, { useState } from "react";
import { Quote, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function About({ profile }) {
    if (!profile) return null;
    return (
        <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="mono-label mb-5">02 · Architecture & Philosophy</div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                            Engineering systems,
                            <br />
                            delivering <span className="text-brand">impact</span>.
                        </h2>
                    </div>

                    <div className="lg:col-span-7 space-y-8">
                        <p className="text-lg text-ink-100 leading-relaxed">{profile.bio}</p>

                        <div className="relative pl-6 border-l-2 border-brand/60">
                            <Quote size={20} className="text-brand mb-3" />
                            <p className="text-base text-ink-200 italic leading-relaxed">
                                &ldquo;Code is an instrument; the true value lies in architecting ecosystems that connect real-world business operations, AI intelligence, and scalable engineering into dependable solutions.&rdquo;
                            </p>
                            <div className="mt-3 mono-label">— {profile.name}</div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4 pt-4">
                            <InfoCard label="Location" value={profile.location} />
                            <InfoCard label="Email" value={profile.email} href={`mailto:${profile.email}`} copyable />
                            <InfoCard label="Experience" value={`${profile.years_experience}+ years`} />
                            <InfoCard label="Availability" value={profile.available_for_hire ? "Open for projects" : "Currently booked"} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function InfoCard({ label, value, href, copyable }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        if (copyable && value) {
            e.preventDefault();
            e.stopPropagation();
            navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`${label} copied to clipboard!`);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const content = (
        <div className="group relative p-5 rounded-lg border border-white/5 bg-ink-800/50 hover:border-brand/40 hover:-translate-y-0.5 transition-all">
            <div className="flex items-center justify-between">
                <div className="mono-label">{label}</div>
                {copyable && (
                    <button
                        type="button"
                        onClick={handleCopy}
                        title={`Copy ${label}`}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-ink-300 hover:text-brand transition-opacity"
                        aria-label={`Copy ${label}`}
                    >
                        {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    </button>
                )}
            </div>
            <div className="mt-2 text-white font-medium truncate">{value}</div>
        </div>
    );
    if (href) return <a href={href}>{content}</a>;
    return content;
}
